import React from "react";
import styles from "./index.module.scss";

const DEFAULT_MIN = 1;
const DEFAULT_MAX = 5;

export interface PollNumbersProps {
  /** Current selected value (0 = none) */
  value: number;
  /** Called when user selects a number */
  onChange: (value: number) => void;
  /** Minimum number (default 1) */
  min?: number;
  /** Maximum number (default 5) */
  max?: number;
  /** Label below the minimum number (e.g. "En kötü") */
  labelMin?: string;
  /** Label below the maximum number (e.g. "En iyi") */
  labelMax?: string;
  /** Disable interaction */
  disabled?: boolean;
  /** Optional className for the root container */
  className?: string;
}

export const PollNumbers: React.FC<PollNumbersProps> = ({
  value,
  onChange,
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  labelMin,
  labelMax,
  disabled = false,
  className,
}) => {
  const count = max - min + 1;
  const numbers = Array.from({ length: count }, (_, i) => min + i);

  const handleClick = (n: number) => {
    if (disabled) return;
    onChange(n);
  };

  const handleKeyDown = (e: React.KeyboardEvent, n: number) => {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onChange(n);
    }
  };

  return (
    <div
      className={`${styles.root} ${disabled ? styles.root_disabled : ""} ${className ?? ""}`}
      role="group"
      aria-label={`Rating: ${value || "none"} from ${min} to ${max}`}
    >
      <div className={styles.numbers}>
        {numbers.map((n) => (
          <button
            key={n}
            type="button"
            className={`${styles.num} ${value === n ? styles.num_selected : ""}`}
            onClick={() => handleClick(n)}
            onKeyDown={(e) => handleKeyDown(e, n)}
            disabled={disabled}
            aria-label={`${n}`}
            aria-pressed={value === n}
          >
            {n}
          </button>
        ))}
      </div>
      {(labelMin != null || labelMax != null) && (
        <div className={styles.labels}>
          {labelMin != null && <span className={styles.labelMin}>{labelMin}</span>}
          {labelMax != null && <span className={styles.labelMax}>{labelMax}</span>}
        </div>
      )}
    </div>
  );
};

export default PollNumbers;
